import { StorageService } from './../storage/storage.service';
import { PERFIL } from './../../config/perfil.config';
import { API_CONFIG } from 'src/app/config/api.config';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

// Mantemos o tipo público para compatibilidade interna
type PermissionKind = 'canAccess' | 'canRemove' | 'canCreateAndEdit' | 'canPublish';

// Mapeia os nomes do front para o enum do backend (TipoPermissao: LER, APAGAR, CRIAR_EDITAR, PUBLICAR)
const KIND_TO_TIPO: Record<PermissionKind, 'LER' | 'APAGAR' | 'CRIAR_EDITAR' | 'PUBLICAR'> = {
  canAccess: 'LER',
  canRemove: 'APAGAR',
  canCreateAndEdit: 'CRIAR_EDITAR',
  canPublish: 'PUBLICAR',
};

@Injectable({ providedIn: 'root' })
export class PermissaoAcessoService {
  private cache = new Map<string, Observable<boolean>>();

  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  // --------- API pública
  canAccess(url: string): Observable<boolean> {
    return this.permissionRequest('canAccess', url);
  }

  canRemove(url: string): Observable<boolean> {
    return this.permissionRequest('canRemove', url);
  }

  canCreateAndEdit(url: string): Observable<boolean> {
    return this.permissionRequest('canCreateAndEdit', url);
  }

  canPublish(url: string): Observable<boolean> {
    return this.permissionRequest('canPublish', url);
  }

  /** Verifica várias rotas de uma vez (agora via /check/bulk) e retorna { [url]: boolean } */
  canAccessMany(urls: string[]): Observable<Record<string, boolean>> {
    
    return this.checkBulk('LER', urls).pipe(
      map(resp => resp?.results ?? {}),
      catchError(() => of({})),
      shareReplay(1)
    );
  }

  /** Usa o endpoint em lote do backend (POST /permissoesacesso/check/bulk) */
  checkBulk(kind: 'LER' | 'APAGAR' | 'CRIAR_EDITAR' | 'PUBLICAR', urls: string[]) {
    return this.http.post<{ results: Record<string, boolean> }>(
      `${API_CONFIG.apiURL}/permissoesacesso/check/bulk`,
      { tipoPermissao: kind, urls, prefixoUrl: `${window.location.protocol}//${window.location.host}` }
    );
  }

  // --------- Internos

  /** Caminho único para todas as permissões + cache por chave */
  private permissionRequest(kind: PermissionKind, url: string): Observable<boolean> {
    const usuario = this.storageService.getUsuarioLogado();

    // Fast-path para MASTER
    if (usuario?.perfil === PERFIL.MASTER) {
      return of(true);
    }

    const tipoPermissao = KIND_TO_TIPO[kind];
    // Normaliza a URL: remove query e hash
    const normalizedUrl = this.normalizeUrl(url);

    // cache por (tipo + url + usuário)
    const cacheKey = `${tipoPermissao}::${normalizedUrl}::${usuario?.id ?? 'anon'}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    const params = new HttpParams()
      .set('tipoPermissao', tipoPermissao)
      .set('urlFuncionalidade', normalizedUrl); // HttpParams faz o encode

    // Novo endpoint unificado com AuthenticationPrincipal
    const endpoint = `${API_CONFIG.apiURL}/permissoesacesso/check`;

    const req$ = this.http.get<boolean>(endpoint, { params }).pipe(
      map(val => !!val),
      catchError(() => of(false)),
      shareReplay(1)
    );

    this.cache.set(cacheKey, req$);
    return req$;
  }

  private normalizeUrl(raw: string): string {
    // Remove querystring e hash, preserva apenas origem + pathname (ou só pathname, se relativo)
    try {
      const parsed = new URL(raw, window?.location?.origin ?? 'http://localhost');
      // Remove barras finais duplicadas (exceto se for só "/")
      const cleanedPath = parsed.pathname.replace(/\/{2,}/g, '/').replace(/(.+)\/+$/, '$1');
      // Se a URL original era absoluta, mantém origin; se era relativa, retorna apenas o path
      const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw);
      return isAbsolute ? `${parsed.origin}${cleanedPath || '/'}` : (cleanedPath || '/');
    } catch {
      // Fallback: corta manualmente em '?' e '#'
      const q = raw.indexOf('?');
      const h = raw.indexOf('#');
      let end = raw.length;
      if (q >= 0) end = Math.min(end, q);
      if (h >= 0) end = Math.min(end, h);
      const base = raw.slice(0, end);
      return base.replace(/\/{2,}/g, '/').replace(/(.+)\/+$/, '$1') || '/';
    }
  }
  
}
